import { useState, Fragment } from 'react';
import { Dropdown } from 'react-bootstrap';
import Creatable from 'react-select/creatable';
import styles from 'widget/event_components/ticket-spot.scss';

export const OptionDropdown = ({ setCustomField, questionText, question, options, errors }) => {
  const isError = !!errors[`custom_${questionText}`];
  const allowAttendeeAdd = question.allow_attendee_add || false;
  const sortAlphabetically = question.sort_options_alphabetically || false;

  // Get custom placeholder or use defaults
  const defaultPlaceholder = allowAttendeeAdd ? 'Select or type to create...' : 'Select an option';
  const placeholder = question.dropdown_placeholder || defaultPlaceholder;

  const [value, setValue] = useState(placeholder);
  const [selectedOption, setSelectedOption] = useState(null);

  // Convert options string to react-select format
  let optionsArray = options
    .split('\n')
    .filter((opt) => opt.trim())
    .map((option) => ({
      value: option,
      label: option,
    }));

  // Sort alphabetically if enabled
  if (sortAlphabetically) {
    optionsArray = optionsArray.sort((a, b) => a.label.localeCompare(b.label));
  }

  // Use react-select Creatable when allow_attendee_add is enabled
  if (allowAttendeeAdd) {
    const handleChange = (newValue) => {
      setSelectedOption(newValue);
      if (newValue) {
        setCustomField('dropdown', question)(questionText, newValue.value);
      }
    };

    const customStyles = {
      control: (provided, state) => {
        // Determine border color based on error and focus state
        let borderColor = '#ced4da';
        if (isError) {
          borderColor = '#dc3545';
        } else if (state.isFocused) {
          borderColor = '#80bdff';
        }

        // Determine box shadow based on focus and error state
        let boxShadow = 'none';
        if (state.isFocused) {
          boxShadow = isError ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : '0 0 0 0.2rem rgba(0, 123, 255, 0.25)';
        }

        return {
          ...provided,
          borderColor,
          boxShadow,
          '&:hover': {
            borderColor: isError ? '#dc3545' : '#80bdff',
          },
          minHeight: '48px',
          height: '48px',
          fontSize: '1rem',
          borderRadius: '4px',
        };
      },
      valueContainer: (provided) => ({
        ...provided,
        height: '48px',
        padding: '0 12px',
      }),
      input: (provided) => ({
        ...provided,
        margin: '0px',
        padding: '0px',
      }),
      indicatorsContainer: (provided) => ({
        ...provided,
        height: '48px',
      }),
      menu: (provided) => ({
        ...provided,
        zIndex: 9999,
      }),
    };

    return (
      <Fragment>
        <Creatable
          isClearable
          value={selectedOption}
          onChange={handleChange}
          options={optionsArray}
          placeholder={placeholder}
          formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
          styles={customStyles}
        />
        {isError && (
          <div className="invalid-feedback" style={{ display: 'block' }}>
            {errors[`custom_${questionText}`]}
          </div>
        )}
      </Fragment>
    );
  }

  // Use regular Bootstrap Dropdown when allow_attendee_add is disabled
  // Prepare options (sorted if needed)
  let regularOptions = options.split('\n').filter((opt) => opt.trim());
  if (sortAlphabetically) {
    regularOptions = regularOptions.sort((a, b) => a.localeCompare(b));
  }

  return (
    <Fragment>
      <Dropdown
        onSelect={(e) => {
          setValue(e);
          setCustomField('dropdown', question)(questionText, e);
        }}
      >
        <Dropdown.Toggle id="dropdown-basic-button" className={styles.ticketSelect}>
          {value}
        </Dropdown.Toggle>
        <Dropdown.Menu className={styles.dropdown}>
          {regularOptions.map((option, index) => (
            <Dropdown.Item eventKey={option} isInvalid={isError} id={`${questionText}--${index}`} key={`${questionText}-${option}`}>
              {option}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
        {isError && (
          <div className="invalid-feedback" style={{ display: 'block' }}>
            {errors[`custom_${questionText}`]}
          </div>
        )}
      </Dropdown>
    </Fragment>
  );
};
